import { Alert, Box, Button, Card, Chip, CircularProgress, Container, IconButton, MenuItem, Stack, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { Device, EldVehicle, MaintenanceWorkOrder, MaintenanceItem, MaintenanceMechanic } from "src/models";
import useModelData, { deleteById, replaceById } from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import SimpleDialog from "src/components/simple-dialog";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";
import DisplayErrors from "src/components/display-errors";
import TruxFormField from "src/components/inputs/trux-form-field";
import PageHeader from "src/components/page-header";
import { addDaysToDate, formatDatetimeForPreview, today } from "src/utils/date-utils";
import TruxDateField from "src/components/inputs/trux-date-field";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import PageFilterLoader, { usePageFilterLoaderData } from "src/components/page-filter-loader";
import NoneFoundReport from "src/components/none-found-report";
import TruxAutocompleteField from "src/components/inputs/trux-autocomplete-field";
import TruxSimpleCheckboxField from "src/components/inputs/trux-simple-checkbox-field";
import { TruxTableCell } from "src/components/trux-table-cell";
import TruxTextField from "src/components/inputs/trux-text-field";
import TruxNumberField from "src/components/inputs/trux-number-field";
import { AuthContext } from "src/auth/context/auth-context";
import TruxSimpleSelectField from "src/components/inputs/trux-simple-select-field";

type MaintenanceWorkOrdersProps = {
  maintenanceWorkOrders?: MaintenanceWorkOrder[],
  onDeleteMaintenanceWorkOrderSelected: (a: MaintenanceWorkOrder) => void,
}

const PerformedMaintenances = memo(({ maintenanceWorkOrders, onDeleteMaintenanceWorkOrderSelected }: MaintenanceWorkOrdersProps) => {
  const { sortedList, sortData } = useSort(maintenanceWorkOrders);

  if (!sortedList) {
    return null;
  }

  return <div>
    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TruxTableCell />

              <SortableHeader sortData={sortData} sortKey="deviceType">
                Vehicle
              </SortableHeader>


              <SortableHeader sortData={sortData} sortKey="maintenanceStatus">
                Status
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="createdAt" align="center">
                Date
              </SortableHeader>

              <SortableHeader sortData={sortData} align="right" sortKey="odometer">
                Odometer
              </SortableHeader>

              <TruxTableCell>
                Maintenance Items
              </TruxTableCell>

              <TruxTableCell>
                Mechanic
              </TruxTableCell>

              <SortableHeader align="right" sortData={sortData} sortKey="postMaintenanceNotes">
                Notes
              </SortableHeader>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedList.map((maintenance, index) =>
              (<TableRow
                key={index}
                hover
                sx={{ cursor: "pointer" }}
              >
                <TruxTableCell align="left">
                  <IconButton color="error" onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onDeleteMaintenanceWorkOrderSelected(maintenance);
                  }}>
                    <Iconify icon="solar:trash-bin-trash-bold" />
                  </IconButton>
                </TruxTableCell>
                <TruxTableCell align="left">
                  {maintenance.deviceType}
                  {maintenance.vin && <><br/>{maintenance.vin}</>}
                </TruxTableCell>


                <TruxTableCell align="left">
                  <MaintenanceStatus status={maintenance.maintenanceStatus} />
                </TruxTableCell>


                <TruxTableCell align="center">
                  {formatDatetimeForPreview(maintenance.createdAt)}
                </TruxTableCell>

                <TruxTableCell align="right">
                  {maintenance.odometer}
                </TruxTableCell>

                <TruxTableCell>
                  {maintenance.maintenanceItemsPerformed?.map((performed, idx) => (
                    <div key={idx}>{performed.item.name}</div>
                  ))}
                </TruxTableCell>

                <TruxTableCell>
                  {maintenance.maintenanceMechanicName || "N/A"}
                </TruxTableCell>

                <TruxTableCell align="right">
                  {maintenance.postMaintenanceNotes}
                </TruxTableCell>
              </TableRow>)
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type SuggestedMaintenanceItem = {
  item: MaintenanceItem;
  isDue: boolean;
  intensity: "error" | "warning" | "success";
  dueIn: string;
}

type PageModel = {
  maintenanceWorkOrders?: MaintenanceWorkOrder[],
  mechanics?: MaintenanceMechanic[],
  devices?: Device[],
  eldVehicles?: EldVehicle[],
  maintenanceItems?: MaintenanceItem[],
}

export default function Page() {
  const settings = useSettingsContext();
  const authContext = useContext(AuthContext);
  const timezone = authContext?.session?.organization?.companyTimezone || "America/Chicago";
  const pageFilterData = usePageFilterLoaderData(
    addDaysToDate(today(timezone), -365),
    addDaysToDate(today(timezone), 365),
  );
  const dataLoader = useCallback(() => ({
    maintenanceWorkOrders: !pageFilterData.fromDate || !pageFilterData.toDate ? [] : axios.get(formatEndpoint(endpoints.maintenanceWorkOrders.findAll, pageFilterData)).then(response => response.data),
    devices: axios.get(endpoints.devices.findAll).then(response => response.data),
    mechanics: axios.get(endpoints.maintenanceMechanics.findAll).then(response => response.data?.filter?.((l: any) => l.active)),
    eldVehicles: axios.get(endpoints.eldVehicles.findAll).then(response => response.data),
    maintenanceItems: axios.get(endpoints.maintenanceItems.findAll).then(response => response.data),
  }), [pageFilterData]);
  const {
    data,
    updateData,
    error,
    loaded,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);

  // Memoized lookup maps for O(1) access instead of O(n) .find() operations
  const devicesById = useMemo(() => {
    const map = new Map<number, Device>();
    data?.devices?.forEach(device => map.set(device.id, device));
    return map;
  }, [data?.devices]);

  const eldVehiclesByDeviceId = useMemo(() => {
    const map = new Map<number, EldVehicle>();
    data?.eldVehicles?.forEach(vehicle => map.set(vehicle.deviceId, vehicle));
    return map;
  }, [data?.eldVehicles]);

  const mechanicsById = useMemo(() => {
    const map = new Map<number, MaintenanceMechanic>();
    data?.mechanics?.forEach(mechanic => map.set(mechanic.id, mechanic));
    return map;
  }, [data?.mechanics]);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const [isOpen, setIsOpen] = useState(false);
  const [deletePerformedMaintenanceOpen, setDeletePerformedMaintenanceOpen] = useState(false);
  const selectedMaintenanceModel = useModelSavingData<MaintenanceWorkOrder>(
    endpoints.maintenanceWorkOrders.update,
    endpoints.maintenanceWorkOrders.create,
    endpoints.maintenanceWorkOrders.delete
  );

  const onDeletePerformedMaintenanceSelected = useCallback((a: MaintenanceWorkOrder) => {
    selectedMaintenanceModel.set(a);
    setDeletePerformedMaintenanceOpen(true);
  }, [selectedMaintenanceModel, setDeletePerformedMaintenanceOpen]);

  const deletePerformedMaintenance = useCallback(() => {
    selectedMaintenanceModel.destroy().then(response => {
      if (response) {
        enqueueSnackbar("Successfully deleted the Performed Maintenance");
        updateData("maintenanceWorkOrders")(deleteById(data!.maintenanceWorkOrders || [], selectedMaintenanceModel.model!));
        setShouldRefresh(true);
      }
    }).catch(() => {
      enqueueSnackbar("Error deleting the Performed Maintenance", { variant: "error" });
    }).finally(() => {
      setDeletePerformedMaintenanceOpen(false);
    });
  }, [selectedMaintenanceModel, updateData, data, setShouldRefresh, setDeletePerformedMaintenanceOpen]);

  const onNewMaintenanceSelected = useCallback(() => {
    selectedMaintenanceModel.set({
      id: 0,
      createdAt: undefined,
      deletedAt: undefined,

      maintenanceDate: today(timezone),
      organizationId: 0,

      maintenanceMechanicId: 0,
      maintenanceMechanicName: "",

      maintenanceStatus: 1,

      deviceId: 0,
      deviceType: "",
      vin: "",

      odometer: 0,
      maintenanceItemsPerformed: [],
      preMaintenanceNotes: "",
      postMaintenanceNotes: "",

      attachments: [],
    });
    setIsOpen(true);
  }, [selectedMaintenanceModel, timezone]);

  const [suggestedMaintenanceItems, setSuggestedMaintenanceItems] = useState([] as SuggestedMaintenanceItem[]);
  const [loadingSuggestedMaintenanceItems, setLoadingSuggestedMaintenanceItems] = useState(false);

  const selectedItemIds = useMemo(() => new Set(selectedMaintenanceModel.model?.maintenanceItemsPerformed.map(i => i.item.id) || []), [selectedMaintenanceModel.model?.maintenanceItemsPerformed]);

  const filteredSuggestedMaintenanceItems = useMemo(() => {
    const intensityPriority: Record<string, number> = { error: 3, warning: 2, success: 1 };

    const itemMap = suggestedMaintenanceItems.reduce((map, suggestedItem) => {
      const existingItem = map.get(suggestedItem.item.id);
      if (!existingItem || intensityPriority[suggestedItem.intensity] > intensityPriority[existingItem.intensity]) {
        map.set(suggestedItem.item.id, suggestedItem);
      }
      return map;
    }, new Map<number, SuggestedMaintenanceItem>());

    return Array.from(itemMap.values());
  }, [suggestedMaintenanceItems]);

  const performMaintenance = () => {
    selectedMaintenanceModel.save().then(response => {
      if (response) {
        enqueueSnackbar("Successfully performed maintenance");
        const maintenanceWorkOrder = response.data as MaintenanceWorkOrder;
        updateData("maintenanceWorkOrders")(replaceById(data!.maintenanceWorkOrders || [], maintenanceWorkOrder));
        setIsOpen(false);
        setSuggestedMaintenanceItems([]);
        setLoadingSuggestedMaintenanceItems(false);
        setShouldRefresh(true);
      }
    });
  }

  const performMaintenanceButton = <Button
    onClick={onNewMaintenanceSelected}
    variant="contained"
    color="success"
    startIcon={<Iconify icon="mingcute:add-line" />}
  >
    Add Maintenance
  </Button>;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        <PageHeader title="Maintenance Work Orders">
          {performMaintenanceButton}
        </PageHeader>

        <PageFilterLoader p={0} pageFilterData={pageFilterData} onRun={() => {
          setShouldRefresh(true);
        }} />

        {data && (data.maintenanceWorkOrders?.length || 0) > 0 && <PerformedMaintenances maintenanceWorkOrders={data.maintenanceWorkOrders} onDeleteMaintenanceWorkOrderSelected={onDeletePerformedMaintenanceSelected} />}
        {loaded && data && data.maintenanceWorkOrders?.length === 0 && <NoneFoundReport />}

        {selectedMaintenanceModel.model && data?.devices && <SimpleDialog
          title="Add Maintenance"
          open={isOpen}
          size="medium"
          onCancel={() => {
            setIsOpen(false);
            setLoadingSuggestedMaintenanceItems(false);
            setSuggestedMaintenanceItems([]);
          }}
          onSubmit={performMaintenance}
          submitDisabled={selectedMaintenanceModel.loading}
          submitButton="Perform Maintenance"
          submitButtonIcon={<Iconify icon="mingcute:check-line" />}
          submitButtonColor="success">

          <DisplayErrors savingData={selectedMaintenanceModel} />
          <Stack direction="column" spacing={1}>
            <TruxFormField label="Vehicle">
              <TruxAutocompleteField
                options={data.devices.map(device => ({
                  label: device.deviceType.replace("ELD Truck #", "Truck #"),
                  id: device.id
                }))}
                onClear={() => {
                  selectedMaintenanceModel.update(model => {
                    model.deviceId = 0;
                    model.vin = "";
                    model.deviceType = "";
                    model.odometer = 0;
                    model.preMaintenanceNotes = "";
                  })
                }}
                onChange={(value) => {
                  const deviceId = value === null ? selectedMaintenanceModel.model?.deviceId || 0 : Number(value.id);
                  const eldVehicle = eldVehiclesByDeviceId.get(deviceId);
                  const device = devicesById.get(deviceId);

                  selectedMaintenanceModel.update(model => {
                    if (eldVehicle) {
                      model.vin = eldVehicle.vin;
                    }
                    if (device) {
                      model.deviceId = deviceId;
                      model.deviceType = device.deviceType;
                      model.odometer = Math.floor(device.lastOdometer) || 0;;
                      model.maintenanceItemsPerformed = [];
                      model.maintenanceStatus = 1;
                    }
                  });

                  if (device) {
                    setLoadingSuggestedMaintenanceItems(true);
                    axios.get(formatEndpoint(endpoints.maintenanceItems.findSuggestedForDevice, { deviceId })).then(response => {
                      setSuggestedMaintenanceItems(response.data);
                    }).catch(err => {
                      console.log(err);
                    }).finally(() => {
                      setLoadingSuggestedMaintenanceItems(false);
                    });
                  }
                }}
                selected={{
                  id: selectedMaintenanceModel.model.deviceId,
                  label: selectedMaintenanceModel.model.deviceType?.replace("ELD Truck #", "Truck #"),
                }}
              />
            </TruxFormField>
            {loadingSuggestedMaintenanceItems ?
              <Box display="flex" mt={1} justifyContent="center" height="40px" overflow="hidden"><CircularProgress /></Box> : <>
              {selectedMaintenanceModel.model?.deviceId > 0 && <>
                {filteredSuggestedMaintenanceItems.length > 0 ? <>
                  <Box sx={{background: "#f4f4f4", borderRadius: 1, p: 2}}>
                    <Box sx={{fontWeight: 600}} mb={2}>Suggested Items</Box>
                    <Stack direction="column" spacing={0.5}>
                      {filteredSuggestedMaintenanceItems.map(({ item, intensity: itemIntensity, dueIn }) => {
                        const intensity = itemIntensity as "error" | "warning" | "success";
                        return (
                          <Stack key={item.id} direction="row" alignItems="center" spacing={1}>
                            <TruxSimpleCheckboxField
                              info
                              value={selectedItemIds.has(item.id)}
                              onChange={() => {
                                const isSelected = selectedItemIds.has(item.id);

                                if (isSelected) {
                                  selectedMaintenanceModel.update(model => {
                                    model.maintenanceItemsPerformed = model.maintenanceItemsPerformed.filter(i => i.item.id !== item.id)
                                  });
                                } else {
                                  selectedMaintenanceModel.update(model => {
                                    model.maintenanceItemsPerformed = [...model.maintenanceItemsPerformed, {
                                      item: {...item},
                                      passed: false,
                                      lastDoneDate: "",
                                      lastDoneMiles: 0,
                                      nextDate: "",
                                      nextMiles: 0
                                    }]
                                  });
                                }
                              }}
                            />
                            <Box component="span">
                              {item.name}
                              {dueIn && <Chip
                                sx={{ml: 1}}
                                label={dueIn}
                                color={intensity}
                                variant={intensity === "warning" ? "outlined" : "filled"}
                                size="small"
                                clickable={false}
                              />}
                            </Box>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>

                  {data.mechanics && data.mechanics.length > 0 && <TruxFormField label="Mechanic">
                    <TruxAutocompleteField
                      options={data?.mechanics?.map(mechanic => ({
                        label: mechanic.mechanicName,
                        id: mechanic.id
                      })) || []}
                      onClear={() => {
                        selectedMaintenanceModel.update(model => {
                          model.maintenanceMechanicName = "";
                          model.maintenanceMechanicId = 0;
                        });
                      }}
                      onChange={(value) => {
                        selectedMaintenanceModel.update(model => {
                          const mechanicId = value === null ? model.maintenanceMechanicId : Number(value?.id);
                          model.maintenanceMechanicId = mechanicId;
                          const mechanic = mechanicsById.get(mechanicId);
                          if (mechanic) {
                            model.maintenanceMechanicName = mechanic.mechanicName;
                          }
                        })
                      }}
                      selected={{
                        id: selectedMaintenanceModel?.model?.maintenanceMechanicId || 0,
                        label: selectedMaintenanceModel?.model?.maintenanceMechanicName || "",
                      }}
                    />
                  </TruxFormField>}

                  <TruxFormField label="Status">
                    <TruxSimpleSelectField value={selectedMaintenanceModel.model?.maintenanceStatus} onChange={(val) => {
                        selectedMaintenanceModel.update((model) => {
                          model.maintenanceStatus = val;
                        })
                      }}>
                        <MenuItem value={0}>Scheduled</MenuItem>
                        <MenuItem value={1}>Done</MenuItem>
                        <MenuItem value={2}>Cancelled</MenuItem>
                      </TruxSimpleSelectField>
                  </TruxFormField>

                  <TruxFormField label="Date">
                    <TruxDateField
                      modelSavingData={selectedMaintenanceModel}
                      field="maintenanceDate"
                    />
                  </TruxFormField>

                  <TruxFormField label="Odometer">
                    <TruxNumberField
                      modelSavingData={selectedMaintenanceModel}
                      field="odometer"
                    />
                  </TruxFormField>

                  <TruxFormField label="Pre Maintenance Notes">
                    <TruxTextField
                      modelSavingData={selectedMaintenanceModel}
                      field="preMaintenanceNotes"
                      minRows={3}
                    />
                  </TruxFormField>

                  <TruxFormField label="Post Maintenance Notes">
                    <TruxTextField
                      modelSavingData={selectedMaintenanceModel}
                      field="postMaintenanceNotes"
                      minRows={3}
                    />
                  </TruxFormField>
                </> : <Alert severity="error">No Maintenance Items apply to this Vehicle</Alert>}
              </>}
            </>}

          </Stack>
        </SimpleDialog>}

        {deletePerformedMaintenanceOpen && (
          <SimpleDialog
          title="Delete Performed Maintenance"
          open={deletePerformedMaintenanceOpen}
          onCancel={() => setDeletePerformedMaintenanceOpen(false)}
          onSubmit={deletePerformedMaintenance}
          submitButton="Delete Performed Maintenance"
          submitButtonIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
          submitButtonColor="error">
            Are you sure you would like to delete this Performed Maintenance?
          </SimpleDialog>
        )}
      </DisplayPageData>
    </Container >
  );
}

function MaintenanceStatus({status}: {status: number}) {
  let label: string;
  let color: "info" | "error" | "success";
  if (status === 0) {
    label = "Scheduled";
    color = "info";
  } else if (status === 1) {
     label = "Performed";
     color = "success";
  } else {
    label = "Cancelled"
    color = "error";
  }

  return <Chip
    sx={{ml: 1}}
    label={label}
    color={color}
    variant="filled"
    size="small"
    clickable={false}
  />;
}